import React, { useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneField } from "@/components/PhoneField";
import { Label } from "@/components/ui/label";
import { Copy, Trash2, Mail, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AssignOwnersModal({ open, onClose, portfolio, headers, onChanged }) {
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "", organization: "", title: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const add = async () => {
    if (!form.email.trim()) { toast.error("Email is required"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/admin/portfolios/${portfolio.id}/owners`, form, { headers });
      setLastInvite(r.data);
      toast.success(r.data.email_mocked ? "Owner invited (email mocked)" : "Owner invited — email sent");
      setForm({ email: "", first_name: "", last_name: "", organization: "", title: "", phone: "" });
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add owner");
    } finally {
      setSaving(false);
    }
  };

  const removeOwner = async (ref) => {
    try {
      await axios.delete(`${API}/api/admin/portfolios/${portfolio.id}/owners/${encodeURIComponent(ref)}`, { headers });
      toast.success("Owner removed");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    }
  };

  const copyLink = (link) => {
    navigator.clipboard?.writeText(link);
    toast.success("Invitation link copied");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="assign-owners-modal" className="max-w-2xl">
        <DialogHeader><DialogTitle>Portfolio Owners — {portfolio?.name}</DialogTitle></DialogHeader>

        {!!(portfolio?.owners || []).length && (
          <div className="space-y-2 rounded-lg border border-slate-200 p-3">
            <div className="text-xs font-medium uppercase text-slate-500">Assigned owners</div>
            {portfolio.owners.map((o) => (
              <div key={o.id} data-testid={`owner-row-${o.id}`} className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2 text-sm">
                <div>
                  <div className="font-medium text-slate-800">{o.user_email}</div>
                  <div className="text-xs text-slate-400">{[o.first_name, o.last_name].filter(Boolean).join(" ")} {o.organization ? `· ${o.organization}` : ""} · <span className={o.status === "active" ? "text-green-600" : "text-amber-600"}>{o.status}</span></div>
                </div>
                <div className="flex items-center gap-1">
                  {o.invite_token && (
                    <button data-testid={`owner-copy-invite-btn-${o.id}`} onClick={() => copyLink(`${window.location.origin}/portfolio/invite/${o.invite_token}`)} title="Copy invite link" className="rounded p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"><Copy className="h-4 w-4" /></button>
                  )}
                  <button data-testid={`owner-remove-btn-${o.id}`} onClick={() => removeOwner(o.id)} title="Remove" className="rounded p-1.5 text-slate-400 hover:bg-red-100 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {lastInvite?.invite_link && (
          <div data-testid="owner-invite-result" className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm">
            <div className="flex items-center gap-2 text-green-700"><CheckCircle2 className="h-4 w-4" /> Invitation created {lastInvite.email_mocked ? "(email mocked — share the link)" : "(email sent)"}</div>
            <div className="mt-2 flex items-center gap-2">
              <Input readOnly value={lastInvite.invite_link} className="h-8 text-xs" data-testid="owner-invite-link" />
              <Button size="sm" variant="outline" onClick={() => copyLink(lastInvite.invite_link)}><Copy className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        )}

        <div className="space-y-3 rounded-lg border border-slate-200 p-3">
          <div className="flex items-center gap-2 text-xs font-medium uppercase text-slate-500"><Mail className="h-3.5 w-3.5" /> Invite a new owner</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label className="text-xs">Email *</Label><Input data-testid="owner-email-input" value={form.email} onChange={set("email")} placeholder="owner@authorizer.org" /></div>
            <div><Label className="text-xs">Organization</Label><Input value={form.organization} onChange={set("organization")} placeholder="NJ DOE" /></div>
            <div><Label className="text-xs">First name</Label><Input value={form.first_name} onChange={set("first_name")} /></div>
            <div><Label className="text-xs">Last name</Label><Input value={form.last_name} onChange={set("last_name")} /></div>
            <div><Label className="text-xs">Title</Label><Input value={form.title} onChange={set("title")} /></div>
            <div><Label className="text-xs">Phone</Label><PhoneField value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} testid="assign-owner-phone" /></div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button data-testid="owner-invite-submit" onClick={add} disabled={saving}>{saving ? "Inviting…" : "Invite Owner"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
